/**
 * Metadata Tab Content
 * Zone library and POI placement for the left panel metadata tab.
 * Drawing tools are handled by PlacementPanel (UX17).
 */

import './MetadataTabContent.css'
import type { TabContentRenderer } from './LeftPanel'
import type { ZoneType, PinType } from '@/types/zone'
import type { ZoneTypeDefinition } from '@/types/zone'
import { FUNCTIONAL_ZONES, PLANTING_ZONES, INFRASTRUCTURE_ZONES, PIN_TYPES } from '@/constants/zoneTypes'
import { getViewer, getSelectedZoneType, setSelectedZoneType, getSelectedPinType, setSelectedPinType } from './state'
import { startWalkthrough } from './Walkthrough'
import { t } from '@/i18n'

type ZoneCategory = 'functional' | 'planting' | 'infrastructure'

const CATEGORY_KEYS: Record<ZoneCategory, { labelKey: string; fallback: string }> = {
  functional: { labelKey: 'metadata.category.functional', fallback: 'Functional' },
  planting: { labelKey: 'metadata.category.planting', fallback: 'Planting' },
  infrastructure: { labelKey: 'metadata.category.infrastructure', fallback: 'Infra' },
}

const CATEGORY_ZONES: Record<ZoneCategory, ZoneTypeDefinition[]> = {
  functional: FUNCTIONAL_ZONES,
  planting: PLANTING_ZONES,
  infrastructure: INFRASTRUCTURE_ZONES,
}

export class MetadataTabContent implements TabContentRenderer {
  private container: HTMLElement | null = null
  private activeCategory: ZoneCategory = 'functional'

  render(container: HTMLElement): void {
    this.container = container
    this.renderContent()
  }

  destroy(): void {
    this.container = null
  }

  onActivate(): void {
    this.updateSelectionStates()
  }

  onDeactivate(): void {
    // nothing
  }

  getSelectedZoneType(): ZoneType | null {
    return getSelectedZoneType()
  }

  private renderContent(): void {
    if (!this.container) return

    const zones = CATEGORY_ZONES[this.activeCategory]

    this.container.innerHTML = `
      <div class="metadata-tab-content">
        <!-- Zone Library -->
        <div class="metadata-section-title">
          <span>${t('metadata.zoneLibrary', 'Zone Library')}</span>
          <button class="tab-help-btn" data-walkthrough="metadata" title="${t('tooltip.help', 'Help')}">
            <i class="fa-solid fa-circle-question"></i>
          </button>
        </div>
        <div class="metadata-zone-library">
          <div class="metadata-category-tabs">
            ${(Object.keys(CATEGORY_KEYS) as ZoneCategory[]).map(cat => `
              <span class="metadata-category-tab${cat === this.activeCategory ? ' active' : ''}" data-category="${cat}">
                ${t(CATEGORY_KEYS[cat].labelKey, CATEGORY_KEYS[cat].fallback)}
              </span>
            `).join('')}
          </div>
          <div class="metadata-zone-grid">
            ${zones.map(z => {
              const colorHex = '#' + z.color.toString(16).padStart(6, '0')
              const isSelected = getSelectedZoneType() === z.type
              return `
                <div class="metadata-zone-item${isSelected ? ' selected' : ''}" data-zone-type="${z.type}">
                  <div class="metadata-zone-color" style="background: ${colorHex};"></div>
                  <span class="metadata-zone-name">${t(z.labelKey, z.label)}</span>
                </div>
              `
            }).join('')}
          </div>
        </div>

        <!-- Points of Interest -->
        <div class="metadata-section-title">${t('metadata.poi', 'Points of Interest')}</div>
        <div class="metadata-poi-library">
          <div class="metadata-poi-grid">
            ${PIN_TYPES.map(p => {
              const colorHex = '#' + p.color.toString(16).padStart(6, '0')
              const isSelected = getSelectedPinType() === p.type
              return `
                <div class="metadata-poi-item${isSelected ? ' selected' : ''}" data-pin-type="${p.type}">
                  <div class="metadata-poi-icon" style="background: ${colorHex}20; color: ${colorHex};">
                    <i class="${p.icon}"></i>
                  </div>
                  <span class="metadata-poi-name">${t(p.labelKey, p.label)}</span>
                </div>
              `
            }).join('')}
          </div>
        </div>

        <!-- Hint Bar -->
        <div class="metadata-hint-bar">
          <i class="fa-solid fa-circle-info"></i>
          <span class="metadata-hint-text">${t('metadata.hintDefault', 'Select a zone type, then draw on canvas')}</span>
        </div>
      </div>
    `

    this.wireEvents()
  }

  private wireEvents(): void {
    if (!this.container) return

    // Help button
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('metadata'))

    // Category tabs
    this.container.querySelectorAll('.metadata-category-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const cat = (tab as HTMLElement).dataset.category as ZoneCategory
        if (cat) {
          this.activeCategory = cat
          this.renderContent()
        }
      })
    })

    // Zone items
    this.container.querySelectorAll('.metadata-zone-item').forEach(item => {
      item.addEventListener('click', () => {
        const type = (item as HTMLElement).dataset.zoneType as ZoneType
        if (type) this.onZoneTypeClick(type)
      })
    })

    // POI items
    this.container.querySelectorAll('.metadata-poi-item').forEach(item => {
      item.addEventListener('click', () => {
        const type = (item as HTMLElement).dataset.pinType as PinType
        if (type) this.onPinTypeClick(type)
      })
    })
  }

  private onZoneTypeClick(type: ZoneType): void {
    const current = getSelectedZoneType()
    if (current === type) {
      setSelectedZoneType(null)
    } else {
      setSelectedZoneType(type)
    }

    // If pin tool was active, switch back to select
    const toolManager = getViewer()?.getToolManager()
    if (toolManager?.getActiveToolName() === 'place-pin') {
      toolManager.setActiveTool('select')
    }

    this.updateSelectionStates()
  }

  private onPinTypeClick(type: PinType): void {
    const current = getSelectedPinType()
    const toolManager = getViewer()?.getToolManager()

    if (current === type) {
      setSelectedPinType(null)
      if (toolManager?.getActiveToolName() === 'place-pin') {
        toolManager.setActiveTool('select')
      }
    } else {
      setSelectedPinType(type)
      toolManager?.setActiveTool('place-pin')
    }

    this.updateSelectionStates()
  }

  private updateSelectionStates(): void {
    if (!this.container) return

    const selectedZone = getSelectedZoneType()
    const selectedPin = getSelectedPinType()

    // Update zone item selection
    this.container.querySelectorAll('.metadata-zone-item').forEach(item => {
      const type = (item as HTMLElement).dataset.zoneType
      item.classList.toggle('selected', type === selectedZone)
    })

    // Update POI item selection
    this.container.querySelectorAll('.metadata-poi-item').forEach(item => {
      const type = (item as HTMLElement).dataset.pinType
      item.classList.toggle('selected', type === selectedPin)
    })
  }
}
